import { faQrcode } from "@fortawesome/free-solid-svg-icons";
import { faCaretDown, faComment, faEnvelope } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Accordion, AccordionDetails, AccordionSummary, Alert, TextField, Typography } from "@mui/material";
import React, { useState } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";
import GlobalStoreContext from "../../../context/GlobalStoreContext";
import Helper from "../../../Helper";
import { Area } from "../../../models/Area";
import { CalendarEvent } from "../../../models/CalendarEvent";
import { Delivery } from "../../../models/Delivery";
import { Equipment } from "../../../models/Equipment";
import { shareProjectItem } from "../../PlotAPI/Share";
import ShareProjectItemManual from "./ShareProjectItemManual";
import ShareProjectItemQRCode from "./shareProjectItemQRCode";

interface Props {
    shareType: "location" | "project" | "delivery" | "equipment";
    projectToken: string;
    onFinish: () => void;
    // Optional props
    currentObject?: Area | Equipment;
    calendarEvent?: CalendarEvent;
    delivery?: Delivery;
}

const ShareProjectItemBody: React.FC<Props> = (props) => {
    const [invitee, setInvitee] = useState<string | null>(null);
    const [maxInvites, setMaxInvites] = useState(false);
    const [shareMethod, setShareMethod] = useState<"email" | "text">("text");

    const globalStoreContext = React.useContext(GlobalStoreContext);

    if (["location", "equipment"].includes(props.shareType) && !props.currentObject) {
        return <></>;
    }

    const onFinish = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        return shareProjectItem(
            globalStoreContext.currentProjectInfo.claimed_by_team_subdomain,
            props.projectToken,
            invitee,
            props.shareType,
            props.currentObject?.id || props.delivery?.id || 0
        ).then(() => {
            props.onFinish();
        });
    };

    const updateInvitees = (value: string) => {
        const recipients = value
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);
        setMaxInvites(recipients.length > 10);
        if (recipients.length <= 10) setInvitee(value);
    };

    const validInviteString = (string: string) => {
        if (shareMethod === "email") {
            const emails = string.split(",").map((email) => email.trim());
            return emails.every((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
        } else {
            const phones = string.split(",").map((phone) => phone.trim());
            return phones.every((phone) => isValidPhoneNumber(`+1${phone}`));
        }
    };

    return (
        <div className={Helper.classNames("flex flex-col gap-4", globalStoreContext.isMobile && "px-6")}>
            <Typography variant="subtitle2" className="text-gray-700 dark:text-plot-darkmode-white" textAlign="center">
                Recipients can view {props.shareType} instantly - no login or app required
            </Typography>

            {maxInvites && <Alert severity="error">Too Many Invites. Only 10 users may be invited at once.</Alert>}

            <ShareProjectItemManual
                shareType={props.shareType}
                projectToken={props.projectToken}
                currentObject={props.currentObject}
                calendarEvent={props.calendarEvent}
                delivery={props.delivery}
            />

            {globalStoreContext.userInfo && (
                <>
                    <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-slate-200" />
                        <p className="text-xs uppercase tracking-wide text-slate-400 font-medium whitespace-nowrap">
                            Or send directly
                        </p>
                        <div className="flex-1 h-px bg-slate-200" />
                    </div>

                    <form onSubmit={onFinish} className="flex flex-col gap-3">
                        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 self-start">
                            <button
                                type="button"
                                onClick={() => setShareMethod("email")}
                                className={Helper.classNames(
                                    "flex items-center gap-2 px-3 py-1.5 rounded-md font-medium text-sm transition-all cursor-pointer",
                                    shareMethod === "email"
                                        ? "bg-white text-plot-orange shadow-sm"
                                        : "text-slate-500 hover:text-slate-700"
                                )}
                            >
                                <FontAwesomeIcon icon={faEnvelope} />
                                Email
                            </button>
                            <button
                                type="button"
                                onClick={() => setShareMethod("text")}
                                className={Helper.classNames(
                                    "flex items-center gap-2 px-3 py-1.5 rounded-md font-medium text-sm transition-all cursor-pointer",
                                    shareMethod === "text"
                                        ? "bg-white text-plot-orange shadow-sm"
                                        : "text-slate-500 hover:text-slate-700"
                                )}
                            >
                                <FontAwesomeIcon icon={faComment} />
                                Text
                            </button>
                        </div>

                        <div className="flex items-start gap-2">
                            <TextField
                                className="flex-1"
                                id="phone_email"
                                label={shareMethod === "email" ? "Enter email address" : "Enter phone number (US only)"}
                                color="warning"
                                variant="filled"
                                onChange={(e) => updateInvitees(e.target.value)}
                                required={true}
                            />
                            <button
                                type="submit"
                                disabled={!invitee || maxInvites || !validInviteString(invitee)}
                                className="shrink-0 cursor-pointer px-6 py-4 bg-linear-to-r from-plot-orange to-[#ffb133] text-white rounded-lg font-semibold text-sm hover:shadow-lg hover:shadow-plot-orange/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
                            >
                                Send
                            </button>
                        </div>
                    </form>
                </>
            )}

            {props.shareType === "project" && globalStoreContext.userInfo && (
                <Alert severity="info">{`This is just a link to view the jobsite Portal. To invite users, go to the Directory tab.`}</Alert>
            )}

            <Accordion>
                <AccordionSummary
                    aria-controls="qr-content"
                    id="qr-header"
                    className="font-plot-open-sans! rounded-lg!"
                    expandIcon={<FontAwesomeIcon icon={faCaretDown} />}
                >
                    <div className="flex items-center">
                        <FontAwesomeIcon icon={faQrcode} className="mr-2" />
                        Share by QR Code
                    </div>
                </AccordionSummary>
                <AccordionDetails>
                    <ShareProjectItemQRCode
                        shareType={props.shareType}
                        currentObject={props.currentObject}
                        calendarEvent={props.calendarEvent}
                        delivery={props.delivery}
                    />
                </AccordionDetails>
            </Accordion>
        </div>
    );
};

export default ShareProjectItemBody;
